import config from "@/config";
import { DataService } from "@/services/data";
import { FundingBasketItem, FundingBasketOverride, FundingBasketUnmatchedItem, FundingEnvelopeUnit } from "@/types/content/funding_basket";

export type AssistantConversation = {
    id: string;
    source_app: string;
    agent_profile: string;
    title: string;
    session_context: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    /** Suggested prompts for an empty chat; from agent profile via API */
    example_questions?: string[] | null;
};

export type AssistantMessage = {
    id: number;
    role: "user" | "assistant" | string;
    content: string;
    created_at: string;
    /** Renseigné pour role === "assistant" ; 1 pouce haut, -1 pouce bas, pas de vote = null */
    feedback_rating?: number | null;
};

/**
 * Référence vers le signal qui a déclenché la suggestion. Permet au panel coach
 * d'afficher au commercial le contexte exact ("Objection prix détectée — «&nbsp;c'est trop cher&nbsp;»")
 * en plus de la réponse suggérée.
 *
 * Côté backend : sérialisé par `_serialize_coach_suggestion_v2` et
 * `_build_coach_payload` (cf. `apps/call_analysis/services/live_coach/dispatch.py`).
 * `null` quand la suggestion ne provient pas d'un signal embedding (ex: layer 2
 * Conductor sans hint, ou source `manual`).
 */
export type CoachSuggestionDetectedSignal = {
    code: string;
    label: string;
    /**
     * Type du signal côté playbook (`Signal.type`). Depuis le refactor
     * "phase milestones" (1.4.x), le Layer 1 Reflex ne traite plus que des
     * objections : les anciens types `behavior`, `risk`, `context`,
     * `milestone` ont été supprimés côté backend (les milestones sont
     * désormais validés par le Layer 2 via `PhaseMilestone`).
     *
     * `null` quand le snapshot playbook n'a pas pu être résolu côté backend
     * (signal inconnu / supprimé) ou pour des suggestions persistées avant
     * l'ajout du champ — dans ces cas on retombe sur le comportement par
     * défaut.
     */
    signal_type: "objection" | null;
    /** Extrait court de l'utterance live qui a matché (≈ 220 chars max). */
    evidence: string | null;
    /** Phrase prototype (`SignalPhrasing.text`) qui a gagné le kNN. */
    matched_phrasing: string | null;
    /** Score cosinus 0..1 (`null` pour les détecteurs non-embedding). */
    similarity_score: number | null;
};

export type CoachSuggestionSource = "layer_1_reflex" | "layer_2_conductor" | "manual" | "system";
export type CoachSuggestionPriority = "low" | "medium" | "high";

/**
 * Famille stratégique d'une `next_best_action` poussée par le Layer 2
 * (Conductor). Aligné sur `ConductorActionType` côté backend (cf.
 * `apps/call_analysis/schemas.py`). Le set est volontairement réduit à des
 * intentions méta — pas des réponses tactiques — pour empêcher le LLM de
 * pondre des verbatim périmés.
 *
 * `null` pour les suggestions L1 reflex (qui n'ont pas d'action_type).
 */
export type CoachActionType = "deepen_discovery" | "reframe_value" | "slow_down" | "let_prospect_talk" | "summarize_and_confirm" | "propose_next_step" | "address_emerging_risk" | "recover_handoff";

/**
 * Suggestion coach unifiée — schéma V2 (`coach.suggestion`).
 *
 * Cette forme est utilisée à la fois par le payload WS et par la réponse REST
 * `/current/coach-suggestions/`.
 */
export type CoachSuggestionPayload = {
    type: "coach.suggestion";
    call_id: string;
    group_id: string;
    /** Wall-clock ms au moment du `shown_at` côté backend. */
    timestamp_ms: number;
    /** ID de la ligne `CoachSuggestion` persistée (unique, sert de clé React). */
    suggestion_id: string;
    /** Code stable de la suggestion (`Suggestion.code`) — null pour les ad-hoc layer 2. */
    suggestion_code: string | null;
    source: CoachSuggestionSource;
    priority: CoachSuggestionPriority;
    /** Phrase courte que le commercial peut dire. À mettre en avant dans l'UI. */
    suggested_answer: string;
    /** Pourquoi cette suggestion : explication courte, à afficher en secondaire. */
    rationale: string | null;
    ttl_seconds: number;
    /** ISO 8601 — la suggestion devient "stale" après. */
    expires_at: string;
    /** Legacy : extrait de l'utterance. Nouveaux consumers : lire `detected_signal.evidence`. */
    evidence: string | null;
    detected_signal: CoachSuggestionDetectedSignal | null;
    /**
     * Famille stratégique de la directive — seulement renseignée pour les
     * suggestions L2 (Conductor). `null` pour les L1 reflex.
     *
     * Permet au front d'appliquer un rendu spécifique L2 (badge "Conseil
     * pilote", icône, libellé humain dérivé du type) au lieu de retomber
     * sur le `suggestion_code` brut qui est synthétique (`l2:deepen_discovery`).
     */
    action_type: CoachActionType | null;
    /**
     * Phrase courte exemple que le commercial peut dire pour reprendre la
     * main. Seulement renseignée par le L2 et uniquement quand
     * `action_type` ∈ {`recover_handoff`, `summarize_and_confirm`}.
     * `null` partout ailleurs — le L2 n'a pas le droit de pondre des
     * verbatim en dehors de ce cas (cf. system prompt Conductor).
     */
    example_handoff_phrase: string | null;
    /** True while a `coach.ask` stream is in progress for this card (client-only). */
    isStreaming?: boolean;
};

export type CoachAskErrorCode =
    | "disabled"
    | "no_active_call"
    | "call_not_owned"
    | "llm_unavailable"
    | "first_token_timeout"
    | "timeout"
    | "empty_response"
    | "cancelled"
    | "internal_error";

export type CoachAskStartMessage = {
    type: "coach.ask.start";
    ask_id: string;
    call_id: string;
    group_id: string;
    model: string;
    timestamp_ms: number;
};

export type CoachAskDeltaMessage = {
    type: "coach.ask.delta";
    ask_id: string;
    text: string;
};

export type CoachAskDoneMessage = {
    type: "coach.ask.done";
    ask_id: string;
    full_text: string;
    suggestion_id: string;
    ttft_ms: number;
    total_ms: number;
    prompt_tokens: number;
    completion_tokens: number;
    model: string;
};

export type CoachAskErrorMessage = {
    type: "coach.ask.error";
    ask_id: string;
    code: CoachAskErrorCode;
    message: string;
};

/** Libellés FR pour les erreurs `coach.ask` — ne pas afficher `message` brut. */
export const COACH_ASK_ERROR_LABELS: Record<CoachAskErrorCode, string> = {
    disabled: "Coach IA indisponible (désactivé par l'administrateur)",
    no_active_call: "Aucun appel en cours",
    call_not_owned: "Cet appel n'est pas le vôtre",
    llm_unavailable: "Coach IA indisponible, réessayez dans un instant",
    first_token_timeout: "Pas de réponse de l'IA, réessayez ?",
    timeout: "L'IA a mis trop de temps, réessayez ?",
    empty_response: "Pas de proposition cette fois, réessayez ?",
    cancelled: "",
    internal_error: "Une erreur est survenue, réessayez ?"
};

export type CoachSuggestionsResponse = {
    call_id: string | null;
    group_id: string | null;
    has_active_call: boolean;
    has_active_desktop: boolean;
    server_time: string;
    suggestions: CoachSuggestionPayload[];
    /**
     * Dernier snapshot `coach.call_state` pour l'appel actif (roadmap des
     * phases, objections, résumé L2). Permet de réhydrater le panel CRM
     * après reconnexion WS sans attendre le prochain tick Conductor.
     */
    call_state: CoachCallStatePayload | null;
    /**
     * Dernier payload `coach.smart_catalog` connu pour l'appel actif —
     * équivaut à un appel `GET /api/v1/coaching/smart-catalog/current/`
     * dont la réponse est embarquée ici pour éviter un round-trip
     * supplémentaire au montage du panel. `null` si aucune génération
     * n'a encore tourné pour le call group courant.
     */
    smart_catalog: CoachSmartCatalogPayload | null;
};

/* ================================================================
 * Smart Catalog — payload `coach.smart_catalog`
 *
 * Miroir TypeScript de la structure produite par
 * `apps/smart_catalog/services/regeneration_orchestrator.py::_build_payload`.
 * Toute modification du contrat backend doit être répercutée ici (et
 * inversement) — le compilateur ne peut pas l'imposer.
 * ================================================================ */

/**
 * Modalité pédagogique d'un produit. Le backend ne valide pas
 * l'enum (champ libre du catalogue Walter), on autorise donc
 * `string` pour ne pas crasher si un nouveau libellé apparaît,
 * tout en gardant l'autocomplétion sur les trois valeurs connues.
 */
export type SmartCatalogModality = "synchrone" | "asynchrone" | "hybride" | (string & {});

export type SmartCatalogProductNextSession = {
    /** ISO 8601 — début de la prochaine session synchrone. */
    starts_at: string;
    /** ISO 8601 — fin de la session. */
    ends_at: string;
    capacity: number | null;
    booked_seats: number;
    /** `null` quand `capacity` est inconnue. */
    seats_left: number | null;
    modality: string;
};

/**
 * Verbatim au niveau thème, généré en Phase B par le LLM.
 * `verbatim_status === "pending"` tant que Phase B n'a pas encore tourné
 * (les champs textes sont vides). Une fois "ready", les champs sont remplis.
 * Le placeholder a une hauteur fixe pour éviter les sauts de page.
 */
export type SmartCatalogThemeVerbatim = {
    transition_sentence: string;
    bullet_1: string;
    bullet_2: string;
    verbatim_status: "pending" | "ready";
};

/**
 * Montant et nature du financement personnel pour un produit.
 *
 * - ``"rac"`` (reste à charge) : un financeur externe couvre une partie
 *   du coût. ``montant`` = prix catalogue − prise en charge financeur.
 * - ``"fp"`` (fonds propres) : pas de subvention externe ; le prospect
 *   paye intégralement (comptant ou via prestataire de paiement, ex. ALMA).
 *   ``montant`` = prix catalogue.
 *
 * ``exact = false`` signifie que le montant est une estimation : le
 * niveau de prise en charge exact n'est confirmé qu'après dépôt d'un
 * dossier (typiquement pour les OPCO).
 */
export type SmartCatalogPersonalFunding = {
    type: "rac" | "fp";
    montant: number | null;
    /**
     * Pour les RAC OPCO sans funding_price négocié : fourchette estimée
     * depuis le FundingRate théorique. ``montant`` vaut ``null`` quand
     * ces deux champs sont renseignés.
     */
    montant_min?: number | null;
    montant_max?: number | null;
    exact: boolean;
};

export type SmartCatalogProduct = {
    product_code: string;
    name: string;
    modality: SmartCatalogModality;
    duration_hours: number | null;
    /** Prix catalogue (listing price). */
    listing_price: number | null;
    /** Prise en charge du financeur (meilleure couverture parmi les PC du produit). */
    funding_price: number | null;
    /**
     * Montant et nature du financement personnel. `null` quand aucun
     * financeur éligible n'est identifié pour ce produit.
     */
    personal_funding: SmartCatalogPersonalFunding | null;
    eligible_fundings: { code: string; label: string; type: string }[];
    /** Score déterministe (calculé côté walter-crm) — pilote l'ordre. */
    score: number;
    /**
     * `true` pour le produit le mieux scoré de son thème. Les produits
     * sont déjà triés par score décroissant, donc c'est le premier de la
     * matrice.
     */
    is_top_pick: boolean;
    /** Prochaines sessions disponibles (places restantes > 0), triées par date. */
    next_sessions: SmartCatalogProductNextSession[];
    /** Note moyenne sur 5 (null si insuffisamment d'avis). */
    ratings_avg?: number | null;
    /** Nombre d'avis clients. */
    ratings_count?: number | null;
};

/**
 * Un thème du Smart Catalogue : une matrice de produits déjà scorés et
 * ordonnés. ``score`` = score MAX parmi les produits du thème (sert à
 * ordonner les thèmes entre eux).
 *
 * ``verbatim`` est généré en Phase B (LLM asynchrone). En Phase A le
 * ``verbatim_status`` est "pending" et les champs textes sont vides ;
 * un événement ``coach.smart_catalog_verbatims`` les remplit.
 *
 * Miroir TypeScript de
 * ``content.services.smart_catalog_pool.group_into_themes`` côté CRM /
 * ``regeneration_orchestrator._serialize_theme`` côté assistant.
 */
export type SmartCatalogTheme = {
    theme_code: string;
    theme_title: string;
    domain: string;
    sub_table: "sante" | "generaliste" | (string & {});
    score: number;
    /** Accroche commerciale du thème (issus du produit le mieux scoré). */
    selling_point: string;
    catchphrase: string;
    verbatim: SmartCatalogThemeVerbatim;
    /** Produits du thème, déjà triés par score décroissant. */
    products: SmartCatalogProduct[];
};

/**
 * Payload de l'événement `coach.smart_catalog_verbatims` (Phase B).
 * Contient les verbatims générés par le LLM, indexés par `theme_code`.
 * Le front les fusionne dans le catalogue déjà affiché.
 */
export type CoachSmartCatalogVerbatimsPayload = {
    type: "coach.smart_catalog_verbatims";
    group_id: string;
    verbatims: Record<string, SmartCatalogThemeVerbatim>;
};

/**
 * État du dernier batch de génération :
 *
 * - `pending` : aucun payload encore disponible (post `call.start` avant
 *   première régénération). Le backend ne pousse jamais ce status — il
 *   peut servir au front pour un état d'attente local si besoin.
 * - `success` : génération complète, tous les verbatims contextualisés.
 * - `degraded` : structure complète mais un ou plusieurs verbatims sont
 *   en repli générique. **Aucun signal visuel d'erreur** sur les lignes.
 * - `failed` : le batch a échoué, le payload précédent reste affiché —
 *   le bouton "Mettre à jour" porte alors le message d'erreur.
 */
export type SmartCatalogStatus = "pending" | "success" | "degraded" | "failed";

/**
 * Origine de la régénération courante. Sert à dériver le libellé de
 * fraîcheur affiché dans l'en-tête.
 */
export type SmartCatalogTrigger = "initial" | "signal_change" | "manual" | "manual_override";

/**
 * Snapshot des signaux conductor que le scoring a vus pour cette régen,
 * enrichi du tryptique détection / override / valeur effective sur les
 * trois axes pilotables (`profession`, `statut professionnel`,
 * `financeur`). Surfacé tel quel par le backend
 * (`regeneration_orchestrator._resolve_signals` →
 * `_build_payload.prospect_signals`) au moment de la génération —
 * toujours cohérent avec le catalogue rendu, pas de drift possible.
 *
 * Sémantique par axe :
 * - `detected_*`   : ce que le conductor a détecté (axe `funding` toujours
 *   vide côté détection — override-only).
 * - `effective_*`  : ce qui a réellement servi au scoring
 *   (`override ?? detected`). C'est la valeur à pré-afficher.
 * - `*_overridden` : `true` quand un override agent prime sur la détection
 *   (badge "Modifié" + bouton reset côté UI).
 *
 * Les clés legacy `prospect_profession_code` / `prospect_professional_status`
 * portent la valeur **effective** (back-compat avec les lecteurs qui ne
 * connaissent pas encore le tryptique).
 *
 * Tous les champs enrichis restent optionnels pour la back-compat avec
 * les payloads pre-override : l'UI doit dégrader silencieusement (pas de
 * filtres, repli sur la bannière "profession détectée") sans crasher.
 */
export type CoachSmartCatalogProspectSignals = {
    prospect_profession_code: string;
    prospect_professional_status?: string;
    engaged_themes: string[];
    detected_profession_code?: string;
    effective_profession_code?: string;
    profession_code_overridden?: boolean;
    detected_professional_status?: string;
    effective_professional_status?: string;
    professional_status_overridden?: boolean;
    detected_funding_code?: string;
    effective_funding_code?: string;
    funding_code_overridden?: boolean;
};

/**
 * Option de picklist `profession` (source CRM `Profession`). `display`
 * filtre déjà côté backend — exposé pour traçabilité.
 */
export type SmartCatalogProfessionOption = {
    code: string;
    name: string;
    display: boolean;
};

/**
 * Option de picklist `statut professionnel` (source CRM
 * `ProfessionalStatus`). `funding_codes` = codes financeurs éligibles via
 * la M2M, doublonné dans `status_to_fundings` pour un accès indexé.
 */
export type SmartCatalogProfessionalStatusOption = {
    code: string;
    name: string;
    display: boolean;
    funding_codes: string[];
};

/** Option de picklist `financeur` (source CRM `FundingOrganism`). */
export type SmartCatalogFundingOrganismOption = {
    code: string;
    name: string;
    type: string;
};

/**
 * Réponse de `GET /api/v1/coaching/smart-catalog/options/` (proxy
 * assistant → CRM, cache court côté backend). Alimente les trois
 * sélecteurs d'override. `status_to_fundings` indexe les financeurs
 * éligibles par code de statut pour scoper le sélecteur financeur.
 */
export type SmartCatalogOptions = {
    professions: SmartCatalogProfessionOption[];
    professional_statuses: SmartCatalogProfessionalStatusOption[];
    funding_organisms: SmartCatalogFundingOrganismOption[];
    status_to_fundings: Record<string, string[]>;
};

/**
 * Corps de `POST /api/v1/coaching/smart-catalog/override/`. Chaque axe
 * est optionnel : seuls les axes présents sont modifiés. Chaîne vide
 * (`""`) ⇒ reset de l'axe (retour à la détection).
 */
export type SmartCatalogOverridePayload = {
    profession_code?: string;
    professional_status?: string;
    funding_code?: string;
};

/**
 * Réponse `202` de l'endpoint override. `overrides` reflète toujours les
 * trois axes (état persisté complet), `enqueued: false` ⇒ régénération
 * coalescée dans un batch déjà en vol (anti-rebond backend).
 */
export type SmartCatalogOverrideResponse = {
    enqueued: boolean;
    group_id: string;
    overrides: {
        profession_code: string;
        professional_status: string;
        funding_code: string;
    };
};

export type CoachSmartCatalogPayload = {
    type: "coach.smart_catalog";
    group_id: string;
    /**
     * Catalog pool keys resolved from the active playbook. Empty when no
     * playbook catalog configuration is available yet — the UI shows a
     * "Profil en cours de qualification" notice in that case.
     */
    catalog_origins: string[];
    trigger_reason: SmartCatalogTrigger;
    status: SmartCatalogStatus;
    /** Wall-clock ms du moment où la génération s'est terminée. */
    generated_at_ms: number;
    prospect_signals?: CoachSmartCatalogProspectSignals;
    /**
     * Thèmes déjà ordonnés par score décroissant (un thème pitché /
     * engagé a disparu de la liste côté backend — clé d'unicité =
     * ``theme_code``). Le front les affiche dans cet ordre, sans tri.
     */
    themes: SmartCatalogTheme[];
};

/* ================================================================
 * Funder table — payload `coach.funding_basket`
 *
 * Miroir TypeScript de
 * `apps/smart_catalog/services/funder_view.py::build_funder_view_payload`.
 * Une ligne par financeur (liste du playbook), avec sa jauge d'enveloppe
 * restante (recalculée par le CRM `funding_basket_preview`) et ses thèmes
 * phares éligibles, ré-ordonnés selon l'ajustement panier.
 * ================================================================ */

/**
 * Origine de la jauge d'un financeur :
 * - `envelope`     : enveloppe `FundingEnvelope` configurée pour le profil.
 * - `default`      : pas d'enveloppe par profil (CPF) — budget par défaut
 *   ajustable par le commercial.
 * - `not_eligible` : financeur demandé (playbook) mais inatteignable pour
 *   le profil — ligne grisée.
 */
export type CoachFunderSource = "envelope" | "default" | "not_eligible";

export type CoachFunderTopProduct = {
    product_code: string;
    name: string;
    /** Modalité du produit phare (`synchronous`, `hybrid`, `asynchronous`). */
    modality: string;
    duration_hours: number | null;
    listing_price: number | null;
    funding_price: number | null;
};

/**
 * Carte thème d'une ligne financeur : thème éligible pour ce financeur,
 * avec son produit phare et un indicateur de tenue dans l'enveloppe
 * restante (`fits` / `overflow`) servant au ré-ordonnancement post-panier.
 */
export type CoachFunderTheme = {
    theme_id: number | null;
    theme_code: string;
    theme_title: string;
    domain: string;
    sub_table: string;
    score: number;
    /** URL directe vers la fiche thème dans le catalogue CRM (null pour les thèmes synthétiques sans ProductTheme). */
    crm_catalog_url: string | null;
    top_product: CoachFunderTopProduct | null;
    /** Consommation estimée du produit phare (heures ou € selon `unit`). */
    consumption: number | null;
    unit: FundingEnvelopeUnit;
    fits: boolean;
    overflow: number;
};

export type CoachFunderRow = {
    funding_code: string;
    funding_label: string;
    unit: FundingEnvelopeUnit;
    source: CoachFunderSource;
    annual_amount: number | null;
    consumed_pre_call: number | null;
    base_remaining: number | null;
    basket_consumption: number | null;
    remaining: number | null;
    remaining_hours: number | null;
    percent_remaining: number | null;
    below_threshold: boolean | null;
    is_doubled: boolean | null;
    hourly_rate: number | null;
    /** Override d'enveloppe agent actif sur ce financeur (null si aucun). */
    override: FundingBasketOverride | null;
    themes: CoachFunderTheme[];
};

export type CoachFundingBasketPayload = {
    type: "coach.funding_basket";
    group_id: string;
    generated_at_ms: number;
    profession_code: string;
    professional_status: string;
    /** False tant que profession + mode d'exercice ne sont pas connus. */
    profile_ready: boolean;
    funding_codes: string[];
    funders: CoachFunderRow[];
    basket: {
        items: FundingBasketItem[];
        unmatched_items: FundingBasketUnmatchedItem[];
    };
};

/**
 * Milestone observable au sein d'une phase. Validé en live par le Layer 2
 * (Conductor) quand le LLM repère que l'objectif est atteint dans le
 * transcript ; persisté côté backend dans `CallState.validated_milestones`
 * (JSON sticky) et dans la timeline `ValidatedMilestoneEvent`.
 *
 * `validated_at_ms` = wall-clock ms de la première utterance qui a
 * matérialisé la validation ; `null` tant que le milestone n'est pas
 * validé. Les milestones sont sticky : une fois `validated: true`, ils
 * ne redescendent jamais à `false` sur le même appel.
 */
export type CoachPhaseMilestone = {
    code: string;
    label: string;
    validated: boolean;
    validated_at_ms: number | null;
};

/**
 * Phase du playbook telle que poussée dans `coach.call_state.phases[]`.
 * Le payload contient la roadmap **complète** à chaque tick (idempotent,
 * pas de réconciliation côté front) — l'ordre est donné par `ordering`,
 * et `is_current` flag la phase active selon le Conductor.
 */
export type CoachPhase = {
    code: string;
    label: string;
    ordering: number;
    objective: string;
    is_current: boolean;
    milestones: CoachPhaseMilestone[];
};

/**
 * Objection détectée pendant l'appel (Layer 1 Reflex, restreint aux
 * objections depuis 1.4.x). Snapshot live des objections actuellement
 * présentes dans `CallState.detected_signals` filtré sur type `objection`.
 *
 * `evidence` = utterance prospect courte qui a déclenché la détection.
 * `last_seen_at_ms` permet d'afficher un "il y a Xs" côté UI.
 */
export type CoachDetectedObjection = {
    code: string;
    label: string;
    last_seen_at_ms: number;
    evidence: string;
};

/**
 * Snapshot stratégique poussé par le Conductor (Layer 2) après chaque tick :
 * phase courante + résumé compressé + roadmap des phases avec milestones +
 * objections détectées. Le panel coach affiche une roadmap interactive,
 * la liste des objections et la liste de suggestions.
 *
 * Émis côté backend par `LayerTwoApplier.apply()` → `push_call_state()` (cf.
 * `apps/call_analysis/services/live_coach/dispatch.py`).
 */
export type CoachCallStatePayload = {
    type: "coach.call_state";
    call_id: string;
    group_id: string;
    timestamp_ms: number;
    /**
     * Code technique du playbook actif (champ `Playbook.code`). Chaîne vide si
     * le playbook n'a pas pu être résolu — l'UI doit dégrader sans crasher.
     */
    playbook_code?: string;
    /**
     * Nom lisible du playbook actif (champ `Playbook.name`). Chaîne vide si
     * le playbook n'a pas pu être résolu — l'UI doit dégrader sans crasher.
     */
    playbook_name?: string;
    /**
     * Catalog pool keys configurés sur le playbook actif (champ
     * `Playbook.catalog_origins`, ex: `["sante"]`, `["generaliste"]`).
     * Sert notamment à n'afficher le tableau financeur que pour les
     * playbooks santé. Optionnel pour la back-compat avec les versions
     * backend qui ne poussent pas encore ce champ — traiter `undefined`
     * comme "domaine inconnu" (dégrader en masquant le tableau financeur).
     */
    catalog_origins?: string[];
    current_phase_code: string;
    /**
     * Libellé humain de la phase courante (ex: "Découverte"). Résolu côté
     * backend depuis le `PlaybookSnapshot`. Chaîne vide si la phase n'a
     * pas pu être résolue (snapshot absent ou code inconnu) — l'UI doit
     * dégrader sans crasher dans ce cas.
     */
    current_phase_label: string;
    /**
     * Objectif de la phase courante tel que défini dans le playbook
     * (champ `Phase.objective`). Chaîne vide si non résolu.
     */
    current_phase_objective: string;
    /**
     * Phase immédiatement suivante dans le pipeline (résolue côté
     * backend via `ordering + 1`). `null` pour la dernière phase ou
     * quand la phase courante n'a pas pu être résolue. Le frontend
     * affiche simplement un hint « Phase suivante : … » sans logique
     * de choix — l'ordre des phases est l'unique source de vérité
     * depuis 1.3.0.
     */
    current_phase_next_phase: { code: string; label: string } | null;
    rolling_summary: string;
    conductor_latency_ms: number;
    conductor_model: string;
    /**
     * Roadmap **complète** des phases pour ce playbook, ordonnée par
     * `ordering`. Chaque phase porte sa propre liste de milestones avec
     * leur état de validation live (sticky, ne redescend jamais à `false`).
     *
     * Optionnel pour la back-compat avec les versions backend pre-1.4.x
     * qui ne poussent pas encore cette section — l'UI doit dégrader en
     * affichant un placeholder dans ce cas, sans crasher.
     */
    phases?: CoachPhase[];
    /**
     * Snapshot live des objections actuellement détectées par le Layer 1
     * Reflex (`CallState.detected_signals` filtré sur type `objection`).
     *
     * Optionnel pour la back-compat avec les versions backend pre-1.4.x
     * qui ne poussent pas encore cette section ; vide quand aucune
     * objection n'a été détectée sur le tick courant.
     */
    detected_objections?: CoachDetectedObjection[];
};

/**
 * Fusionne un payload `coach.smart_catalog` entrant avec l'état local.
 *
 * Phase A reconstruit le catalogue sans verbatims (placeholders vides,
 * `verbatim_status: "pending"`). Si le previous avait déjà des verbatims
 * `ready` pour certains thèmes, on les réinjecte dans le payload entrant
 * afin d'éviter que l'UI repasse en skeleton pour des verbatims déjà
 * affichés. Seuls les thèmes réellement nouveaux (absent du previous ou
 * sans verbatim ready) conservent leur placeholder.
 *
 * Les thèmes présents dans `previous` mais absents de `incoming` sont
 * simplement supprimés (le backend a retiré le thème du catalogue).
 */
export function mergeSmartCatalogVerbatims(
    previous: CoachSmartCatalogPayload | null,
    incoming: CoachSmartCatalogPayload
): CoachSmartCatalogPayload {
    if (!previous) return incoming;
    const readyByCode = new Map<string, SmartCatalogThemeVerbatim>();
    for (const theme of previous.themes) {
        if (theme.verbatim?.verbatim_status === "ready") {
            readyByCode.set(theme.theme_code, theme.verbatim);
        }
    }
    if (readyByCode.size === 0) return incoming;
    return {
        ...incoming,
        themes: incoming.themes.map(theme => {
            if (theme.verbatim?.verbatim_status === "ready") return theme;
            const savedVerbatim = readyByCode.get(theme.theme_code);
            if (!savedVerbatim) return theme;
            return { ...theme, verbatim: savedVerbatim };
        })
    };
}

/**
 * Fusionne un snapshot `coach.call_state` entrant avec l'état local.
 *
 * Le backend peut renvoyer `phases: []` quand le PlaybookSnapshot figé
 * n'est plus résolvable (cache invalidé, version archivée mid-call).
 * Un remplacement brut effacerait la roadmap déjà affichée — on conserve
 * les phases précédentes tant que le nouvel event n'en apporte pas.
 */
export function mergeCoachCallState(previous: CoachCallStatePayload | null, incoming: CoachCallStatePayload): CoachCallStatePayload {
    const incomingPhases = incoming.phases ?? [];
    const keepPreviousPhases = incomingPhases.length === 0 && Boolean(previous?.phases?.length);
    return {
        ...incoming,
        phases: keepPreviousPhases ? previous!.phases : incoming.phases
    };
}

export type AssistantWsMessage =
    | {
          type: "coach.suggestion";
          call_id: string;
          group_id: string;
          timestamp_ms: number;
          suggestion_id: string;
          suggestion_code: string | null;
          source: CoachSuggestionSource;
          priority: CoachSuggestionPriority;
          suggested_answer: string;
          rationale: string | null;
          ttl_seconds: number;
          expires_at: string;
          evidence: string | null;
          detected_signal: CoachSuggestionDetectedSignal | null;
          action_type: CoachActionType | null;
          example_handoff_phrase: string | null;
      }
    | {
          type: "call.start";
          call_id: string;
          intake_mode: string;
          endpoints?: Record<string, string>;
          metadata?: Record<string, unknown>;
      }
    | {
          type: "call.end";
          call_id: string;
      }
    | {
          /**
           * Émis par le backend dès qu'une `DesktopControlConsumer` desktop
           * arrive (`online: true`) ou s'en va (`online: false`). Permet à
           * l'UI du CRM web de réagir instantanément à un logout/cmd-Q sur
           * l'app desktop — sans dépendre d'un sweep TTL.
           */
          type: "desktop.presence";
          online: boolean;
      }
    | (CoachCallStatePayload & { type: "coach.call_state" })
    | (CoachSmartCatalogPayload & { type: "coach.smart_catalog" })
    | CoachSmartCatalogVerbatimsPayload
    | (CoachFundingBasketPayload & { type: "coach.funding_basket" })
    | { type: "session.replaced" }
    | { type: "config.reload" }
    | CoachAskStartMessage
    | CoachAskDeltaMessage
    | CoachAskDoneMessage
    | CoachAskErrorMessage;

export type CoachSuggestionContext = { callId: string; groupId: string };

export type AssistantWsHandlers = {
    /**
     * Reçoit chaque suggestion `coach.suggestion` remontée du backend. La
     * dédup éventuelle est faite par le consumer en clé sur `suggestion_id`.
     */
    onSuggestion?: (suggestion: CoachSuggestionPayload, ctx: CoachSuggestionContext) => void;
    /**
     * Reçoit chaque snapshot stratégique `coach.call_state` poussé par le
     * Layer 2 (Conductor). Le dernier reçu remplace toujours le précédent —
     * il n'y a qu'un seul état pilote actif à la fois côté UI.
     */
    onCallState?: (state: CoachCallStatePayload) => void;
    /**
     * Reçoit chaque payload `coach.smart_catalog` poussé par le backend
     * (`apps/smart_catalog/services/dispatch.py`). Le dernier reçu
     * remplace toujours le précédent — il n'y a qu'un catalogue actif
     * à un instant T.
     */
    onSmartCatalog?: (payload: CoachSmartCatalogPayload) => void;
    /**
     * Reçoit les verbatims générés en Phase B (`coach.smart_catalog_verbatims`).
     * Indexés par `theme_code`, à fusionner dans le catalogue déjà affiché.
     */
    onSmartCatalogVerbatims?: (payload: CoachSmartCatalogVerbatimsPayload) => void;
    /**
     * Reçoit chaque payload `coach.funding_basket` (tableau financeur)
     * poussé par le backend après une régénération Smart Catalog, une
     * mutation panier ou un override d'enveloppe. Le dernier remplace
     * toujours le précédent.
     */
    onFundingBasket?: (payload: CoachFundingBasketPayload) => void;
    onCallStart?: (event: Extract<AssistantWsMessage, { type: "call.start" }>) => void;
    onCallEnd?: (callId: string) => void;
    onDesktopPresence?: (online: boolean) => void;
    onSessionReplaced?: () => void;
    onConfigReload?: () => void;
    onAuthFailure?: () => void;
    onConnectionChange?: (state: "connecting" | "open" | "closed") => void;
    onAskStart?: (event: CoachAskStartMessage) => void;
    onAskDelta?: (event: CoachAskDeltaMessage) => void;
    onAskDone?: (event: CoachAskDoneMessage) => void;
    onAskError?: (event: CoachAskErrorMessage) => void;
};

export type AssistantWsConnection = {
    /** Ferme proprement la connexion. Pas de reconnexion automatique après cet appel. */
    close: () => void;
    /**
     * Déclenche un flux `coach.ask` ("Qu'est-ce que je dois dire ?").
     * Bufferise la requête si la socket n'est pas encore ouverte.
     */
    ask: (callId?: string) => string;
};

export type AssistantCallEventPayload = {
    event_type: "call.answered" | "call.ended" | "call.created" | "call.missed";
    voip_provider: "ringover" | "diabolocom";
    voip_call_id: string;
    agent_email: string;
    agent_first_name: string | null;
    agent_last_name: string | null;
    agent_process: string | null;
    record_id: string | null;
    record_type: "opportunity" | "lead" | "account" | "case" | null;
    prospect_phone?: string;
    occurred_at: string;
    metadata?: Record<string, unknown>;
};

export type AssistantDesktopDownloadLinks = {
    mac?: string;
    windows?: string;
};

type PaginatedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

type AssistantJwtTokenResponse = {
    access_token: string;
    token_type: string;
    expires_in: number;
    expires_at: string;
    agent_profile: string;
};

export type AssistantSseEvent = {
    eventType: string;
    data: unknown;
    raw: string;
    receivedAt: string;
};

/** Payload SSE `tool_activity` (MCP / réflexion) — aligné sur le provider OpenAI Responses. */
export type AssistantToolActivityData = {
    phase?: string;
    tool_name?: string;
    server_label?: string;
    item_id?: string;
};

/** Libellé court pour l’UI (bulle d’attente) à partir d’un événement `tool_activity`. */
export function formatAssistantToolActivityLine(data: AssistantToolActivityData | Record<string, unknown>): string {
    const phase = String(data.phase ?? "");
    const tool = String(data.tool_name ?? "").trim();
    const server = String(data.server_label ?? "").trim();
    switch (phase) {
        case "reasoning_in_progress":
            return "Réflexion…";
        case "mcp_list_tools_in_progress":
            return "Connexion au serveur MCP (liste des outils)…";
        case "mcp_list_tools_completed":
            return "Outils MCP chargés.";
        case "mcp_list_tools_failed":
            return "Échec du chargement des outils MCP.";
        case "mcp_list_tools_item":
            return server ? `Préparation MCP (${server})…` : "Préparation MCP…";
        case "mcp_call_discovered":
            return tool ? `Outil : ${tool}` : "Appel d’un outil MCP…";
        case "mcp_call_in_progress":
            return tool ? `Exécution de ${tool}…` : "Exécution d’un outil MCP…";
        case "mcp_call_completed":
            return tool ? `${tool} terminé.` : "Outil MCP terminé.";
        case "mcp_call_failed":
            return tool ? `Échec de ${tool}.` : "Échec d’un outil MCP.";
        default:
            return phase ? `Activité : ${phase}` : "Activité en cours…";
    }
}

export class AssistantApiError extends Error {
    readonly status: number;
    readonly data: unknown;

    constructor(message: string, status: number, data: unknown) {
        super(message);
        this.name = "AssistantApiError";
        this.status = status;
        this.data = data;
    }
}

function parseJsonOrRaw(payload: string): unknown {
    if (!payload.trim()) return null;
    try {
        return JSON.parse(payload);
    } catch {
        return payload;
    }
}

function joinUrl(baseUrl: string, path: string): string {
    const cleanBase = baseUrl.trim().replace(/\/+$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBase}${normalizedPath}`;
}

export function parseSseChunk(chunk: string): { events: Array<{ eventType: string; data: unknown; raw: string }>; rest: string } {
    const normalizedChunk = chunk.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const blocks = normalizedChunk.split("\n\n");
    const rest = blocks.pop() ?? "";
    const events: Array<{ eventType: string; data: unknown; raw: string }> = [];

    for (const block of blocks) {
        const lines = block
            .split("\n")
            .map(line => line.trimEnd())
            .filter(Boolean);
        if (!lines.length) continue;

        let eventType = "message";
        const dataLines: string[] = [];
        for (const line of lines) {
            if (line.startsWith("event:")) {
                eventType = line.slice(6).trim() || "message";
            } else if (line.startsWith("data:")) {
                dataLines.push(line.slice(5).trim());
            }
        }
        events.push({
            eventType,
            data: parseJsonOrRaw(dataLines.join("\n")),
            raw: block
        });
    }

    return { events, rest };
}

function getAssistantApiBaseUrl() {
    const stage = (import.meta.env.VITE_RELEASE_STAGE as keyof typeof config.WALTER_ASSISTANT_BACKEND_URL) || "development";
    const envOverride = String(import.meta.env.VITE_WALTER_ASSISTANT_BACKEND_URL || "").trim();
    return envOverride || config.WALTER_ASSISTANT_BACKEND_URL[stage];
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function desktopPlatformFromKey(key: string): keyof AssistantDesktopDownloadLinks | null {
    const platformKey = key.toLowerCase();
    if (platformKey.includes("darwin") || platformKey.includes("macos") || platformKey.includes("mac")) {
        return "mac";
    }
    if (platformKey.includes("windows") || platformKey.includes("win32")) {
        return "windows";
    }
    return null;
}

function extractDesktopDownloadLinks(manifest: unknown): AssistantDesktopDownloadLinks {
    if (!isRecord(manifest)) return {};

    const links: AssistantDesktopDownloadLinks = {};
    const candidates: Array<[string, unknown]> = [];
    if (isRecord(manifest.platforms)) {
        candidates.push(...Object.entries(manifest.platforms));
    } else {
        candidates.push(...Object.entries(manifest));
    }

    for (const [key, value] of candidates) {
        if (!isRecord(value) || typeof value.url !== "string") continue;
        const platform = desktopPlatformFromKey(key);
        if (!platform || links[platform]) continue;
        const url = value.url;
        links[platform] = url;
    }

    return links;
}

class AssistantTokenManager {
    private accessToken: string | null = null;
    private expiresAtMs: number | null = null;

    async getValidToken(forceRefresh: boolean = false): Promise<string> {
        const nowMs = Date.now();
        const refreshBufferMs = 90 * 1000;
        const hasUsableToken = this.accessToken && this.expiresAtMs && this.expiresAtMs - nowMs > refreshBufferMs;
        if (!forceRefresh && hasUsableToken && this.accessToken) {
            return this.accessToken;
        }

        const tokenResponse = await DataService.client.url("/users/walter-assistant/token/").post({}).json<AssistantJwtTokenResponse>();
        this.accessToken = tokenResponse.access_token;
        this.expiresAtMs = new Date(tokenResponse.expires_at).getTime();
        if (!this.accessToken) {
            throw new AssistantApiError("Assistant token is missing", 500, tokenResponse);
        }
        return this.accessToken;
    }

    clear() {
        this.accessToken = null;
        this.expiresAtMs = null;
    }
}

export class WalterAssistantClient {
    private tokenManager = new AssistantTokenManager();
    private baseUrl = getAssistantApiBaseUrl();

    private async request<T>(path: string, init: RequestInit = {}, allowAuthRetry: boolean = true): Promise<T> {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            const token = await this.tokenManager.getValidToken(attempt > 0);
            const response = await fetch(joinUrl(this.baseUrl, path), {
                ...init,
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    ...(init.headers || {})
                }
            });

            const payloadText = await response.text();
            const payload = parseJsonOrRaw(payloadText);
            if (response.ok) {
                return payload as T;
            }

            const shouldRetry = allowAuthRetry && attempt === 0 && (response.status === 401 || response.status === 403);
            if (shouldRetry) {
                this.tokenManager.clear();
                continue;
            }

            throw new AssistantApiError(`Assistant API error (${response.status})`, response.status, payload);
        }

        throw new AssistantApiError("Assistant API auth error", 401, null);
    }

    async checkAuth() {
        return this.request<{
            user_id: number;
            agent_profile: string;
            agent_profile_capabilities: {
                identifier: string;
                name: string;
                description: string;
            } | null;
        }>("/api/v1/auth/me", { method: "GET" });
    }

    async getCurrentCoachSuggestions() {
        return this.request<CoachSuggestionsResponse>("/api/calls/current/coach-suggestions/", { method: "GET" });
    }

    /**
     * Hydrate le panel Smart Catalog : récupère le dernier payload connu
     * pour l'appel actif de l'utilisateur. À utiliser au boot et après
     * reconnexion WS si on n'a pas déjà l'info via la réponse
     * `getCurrentCoachSuggestions()` (qui embarque le payload en bonus).
     *
     * Renvoie `{ group_id: null, smart_catalog: null }` si aucun appel
     * actif, et `smart_catalog: null` si la première génération n'a pas
     * encore tourné — l'UI doit dégrader sans crasher.
     */
    async fetchCurrentSmartCatalog() {
        return this.request<{
            group_id: string | null;
            server_time?: string;
            smart_catalog: CoachSmartCatalogPayload | null;
        }>("/api/v1/coaching/smart-catalog/current/", { method: "GET" });
    }

    /**
     * Bouton "Mettre à jour le catalogue" — enqueue une régénération
     * manuelle. Le résultat arrive par WS sur `coach.smart_catalog`,
     * pas dans la réponse HTTP. `enqueued: false` ⇒ la requête a été
     * coalescée dans un batch déjà en vol (anti-rebond backend).
     */
    async refreshSmartCatalog() {
        return this.request<{ enqueued: boolean; group_id: string }>(
            "/api/v1/coaching/smart-catalog/refresh/",
            { method: "POST" }
        );
    }

    /**
     * Picklists des trois axes d'override (profession, statut
     * professionnel, financeur) + mapping statut→financeurs. À récupérer
     * une fois au boot du panel — le backend met en cache court (les
     * listes bougent rarement). `502 options_unavailable` quand le CRM
     * est indisponible : l'UI dégrade en masquant les filtres.
     */
    async fetchSmartCatalogOptions() {
        return this.request<SmartCatalogOptions>(
            "/api/v1/coaching/smart-catalog/options/",
            { method: "GET" }
        );
    }

    /**
     * Override agent d'un ou plusieurs axes du Smart Catalog. Sticky et
     * prioritaire sur la détection conductor. Chaîne vide ⇒ reset de
     * l'axe. Le résultat (nouveau classement) arrive par WS sur
     * `coach.smart_catalog`, pas dans la réponse HTTP — on bascule juste
     * `isSmartCatalogPending` côté provider. `404 no_active_call` si
     * aucun appel actif, `400 invalid_code` si un code n'appartient pas
     * à la picklist.
     */
    async overrideSmartCatalog(payload: SmartCatalogOverridePayload) {
        return this.request<SmartCatalogOverrideResponse>(
            "/api/v1/coaching/smart-catalog/override/",
            { method: "POST", body: JSON.stringify(payload) }
        );
    }

    /**
     * Demande la génération on-demand de verbatims pour une liste de theme_codes.
     *
     * Appelé quand le front révèle un nouveau lot de thèmes via "Voir plus"
     * et que certains de ces thèmes ont encore ``verbatim_status: "pending"``.
     * Les verbatims générés arrivent par WS sur `coach.smart_catalog_verbatims`
     * — pas dans la réponse HTTP.
     */
    async requestVerbatims(theme_codes: string[]) {
        return this.request<{ enqueued: boolean; group_id: string }>(
            "/api/v1/coaching/smart-catalog/request-verbatims/",
            { method: "POST", body: JSON.stringify({ theme_codes }) }
        );
    }

    /**
     * Hydrate le tableau financeur : recalcule la vue par financeur pour
     * l'appel actif (jauges + thèmes phares + panier). `funding_basket:
     * null` quand le playbook n'expose aucun financeur (table masquée) ou
     * sans appel actif.
     */
    async fetchCurrentFundingBasket() {
        return this.request<{
            group_id: string | null;
            server_time?: string;
            funding_basket: CoachFundingBasketPayload | null;
        }>("/api/v1/coaching/smart-catalog/funding-basket/current/", { method: "GET" });
    }

    /**
     * Ajoute / retire une formation du panier financeur. Le backend mute
     * le `FundingBasketState` persistant, recalcule la vue et la pousse
     * par WS — la réponse HTTP embarque aussi le payload recalculé pour
     * une mise à jour optimiste immédiate.
     */
    async updateFundingBasketItem(input: { action: "add" | "remove" } & FundingBasketItem) {
        return this.request<{ group_id: string; funding_basket: CoachFundingBasketPayload | null }>(
            "/api/v1/coaching/smart-catalog/funding-basket/items/",
            { method: "POST", body: JSON.stringify(input) }
        );
    }

    /**
     * Définit ou réinitialise un override d'enveloppe par financeur
     * (budget par défaut CPF corrigé, etc.). `reset: true` efface
     * l'override. Le résultat est recalculé et renvoyé (et poussé par WS).
     */
    async updateFundingBasketEnvelopeOverride(input: { funding_code: string; mode?: FundingBasketOverride["mode"]; amount?: number; reset?: boolean }) {
        return this.request<{ group_id: string; funding_basket: CoachFundingBasketPayload | null }>(
            "/api/v1/coaching/smart-catalog/funding-basket/envelope-override/",
            { method: "POST", body: JSON.stringify(input) }
        );
    }

    /**
     * Demande l'intervention humaine d'un coach sur l'appel en cours.
     *
     * Côté backend (à implémenter par le CTO) : l'endpoint doit notifier le
     * coach disponible (Slack / push / etc.) avec le contexte de l'appel
     * courant (call_id, phase courante, dernières utterances) pour qu'il
     * puisse rejoindre le commercial rapidement.
     */
    async requestCoachHelp() {
        return this.request<{ status: string }>("/api/calls/current/coach-help/", { method: "POST" });
    }

    /**
     * Liste tous les playbooks actifs (accessible aux clients web JWT).
     * Utilisé par le sélecteur de playbook dans le drawer live coach.
     */
    async listPlaybooks() {
        return this.request<{ code: string; name: string }[]>(
            "/api/calls/current/available-playbooks/",
            { method: "GET" }
        );
    }

    /**
     * Change de playbook en cours d'appel. Remet le state à la première
     * phase du nouveau playbook et push un `coach.call_state` par WebSocket.
     *
     * `404 no_active_call` si aucun appel actif, `404 not_found` si le
     * playbook n'existe pas.
     */
    async switchPlaybook(playbookCode: string) {
        return this.request<{ playbook_code: string; playbook_name: string }>(
            "/api/calls/current/switch-playbook/",
            { method: "POST", body: JSON.stringify({ playbook_code: playbookCode }) }
        );
    }

    /**
     * Ouvre un WebSocket vers `/ws/desktop/` pour recevoir les conseils en temps réel.
     *
     * - Authentification : JWT court récupéré via {@link AssistantTokenManager},
     *   passé en query param `?token=` car les navigateurs ne posent pas de
     *   header sur le handshake WebSocket.
     * - Reconnexion : backoff exponentiel borné à 30s avec jitter ±25%.
     * - 4001 (auth) : on rafraîchit le JWT et on tente une seule reconnexion.
     * - `session.replaced` : on n'essaie **pas** de reconnecter (évite la boucle
     *   de "vol de socket" entre deux clients du même utilisateur).
     */
    openCoachSocket(handlers: AssistantWsHandlers): AssistantWsConnection {
        let socket: WebSocket | null = null;
        let retryAttempt = 0;
        let reconnectTimer: number | null = null;
        let closedByUs = false;
        let sessionReplaced = false;
        let authRefreshAttempted = false;
        let pendingAsk: { type: "coach.ask"; ask_id: string; call_id?: string } | null = null;

        const buildWsUrl = (token: string): string => {
            const httpUrl = joinUrl(this.baseUrl, "/ws/desktop/");
            const wsUrl = new URL(httpUrl);
            wsUrl.protocol = wsUrl.protocol === "https:" ? "wss:" : "ws:";
            wsUrl.searchParams.set("token", token);
            return wsUrl.toString();
        };

        const scheduleReconnect = (force: boolean = false) => {
            if (closedByUs || sessionReplaced) return;
            if (reconnectTimer !== null) return;

            const base = Math.min(30_000, 1000 * 2 ** retryAttempt);
            const jitter = base * (0.75 + Math.random() * 0.5);
            retryAttempt += 1;
            reconnectTimer = window.setTimeout(() => {
                reconnectTimer = null;
                void connect(force);
            }, jitter);
        };

        const connect = async (forceRefresh: boolean = false): Promise<void> => {
            if (closedByUs || sessionReplaced) return;
            handlers.onConnectionChange?.("connecting");

            let token: string;
            try {
                token = await this.tokenManager.getValidToken(forceRefresh);
            } catch (error) {
                console.error("Walter Assistant WS auth error:", error);
                handlers.onAuthFailure?.();
                handlers.onConnectionChange?.("closed");
                return;
            }

            /*
             * Re-check après le `await` : `close()` peut avoir été appelé pendant
             * le fetch du token (typique en React StrictMode où l'effet se monte,
             * se nettoie, puis se remonte). Sans cette barrière, on créerait un
             * WebSocket pour une connexion déjà annulée, qui se ferait alors
             * déplacer par le remount via `session.replaced` → faux positif.
             */
            if (closedByUs || sessionReplaced) {
                handlers.onConnectionChange?.("closed");
                return;
            }

            const url = buildWsUrl(token);
            const ws = new WebSocket(url);
            socket = ws;

            ws.addEventListener("open", () => {
                retryAttempt = 0;
                authRefreshAttempted = false;
                handlers.onConnectionChange?.("open");
                if (pendingAsk && ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify(pendingAsk));
                    pendingAsk = null;
                }
            });

            ws.addEventListener("message", event => {
                let msg: AssistantWsMessage;
                try {
                    msg = JSON.parse(event.data) as AssistantWsMessage;
                } catch {
                    return;
                }
                dispatch(msg, handlers);
            });

            ws.addEventListener("close", event => {
                socket = null;
                handlers.onConnectionChange?.("closed");
                if (closedByUs || sessionReplaced) return;

                if (event.code === 4001) {
                    if (!authRefreshAttempted) {
                        authRefreshAttempted = true;
                        this.tokenManager.clear();
                        retryAttempt = 0;
                        scheduleReconnect(true);
                    } else {
                        handlers.onAuthFailure?.();
                    }
                    return;
                }

                scheduleReconnect();
            });

            ws.addEventListener("error", () => {
                // Will be followed by a close event; reconnection is handled there.
            });
        };

        const dispatch = (msg: AssistantWsMessage, h: AssistantWsHandlers) => {
            switch (msg.type) {
                case "coach.suggestion": {
                    /*
                     * On strip le champ `type` qui n'est utile que pour le
                     * switch — le payload reste de la même shape.
                     */
                    const { type: _type, call_id, group_id, ...rest } = msg;
                    h.onSuggestion?.({ type: "coach.suggestion", call_id, group_id, ...rest }, { callId: call_id, groupId: group_id });
                    break;
                }
                case "coach.call_state":
                    h.onCallState?.(msg);
                    break;
                case "coach.smart_catalog":
                    h.onSmartCatalog?.(msg);
                    break;
                case "coach.smart_catalog_verbatims":
                    h.onSmartCatalogVerbatims?.(msg);
                    break;
                case "coach.funding_basket":
                    h.onFundingBasket?.(msg);
                    break;
                case "call.start":
                    h.onCallStart?.(msg);
                    break;
                case "call.end":
                    h.onCallEnd?.(msg.call_id);
                    break;
                case "desktop.presence":
                    h.onDesktopPresence?.(msg.online);
                    break;
                case "session.replaced":
                    /*
                     * Garde-fou anti-faux-positif : si l'instance locale est déjà
                     * en train de se fermer (typique du double-mount StrictMode),
                     * un `session.replaced` reçu sur cette socket est très
                     * probablement notre propre remount qui nous a déplacé →
                     * ignorer pour ne pas bloquer le provider en "Reprendre ici".
                     */
                    if (closedByUs) break;
                    sessionReplaced = true;
                    h.onSessionReplaced?.();
                    break;
                case "config.reload":
                    h.onConfigReload?.();
                    break;
                case "coach.ask.start":
                    h.onAskStart?.(msg);
                    break;
                case "coach.ask.delta":
                    h.onAskDelta?.(msg);
                    break;
                case "coach.ask.done":
                    h.onAskDone?.(msg);
                    break;
                case "coach.ask.error":
                    h.onAskError?.(msg);
                    break;
            }
        };

        const ask = (callId?: string): string => {
            const askId = crypto.randomUUID();
            const payload = { type: "coach.ask" as const, ask_id: askId, ...(callId ? { call_id: callId } : {}) };
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify(payload));
            } else {
                pendingAsk = payload;
            }
            return askId;
        };

        void connect();

        return {
            close: () => {
                closedByUs = true;
                pendingAsk = null;
                if (reconnectTimer !== null) {
                    window.clearTimeout(reconnectTimer);
                    reconnectTimer = null;
                }
                if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
                    socket.close(1000, "client_close");
                }
                socket = null;
            },
            ask
        };
    }

    async postCallEvent(payload: AssistantCallEventPayload) {
        return this.request<void>("/api/calls/events/", {
            method: "POST",
            body: JSON.stringify(payload)
        });
    }

    async getDesktopDownloadLinks() {
        const manifest = await this.request<unknown>("/desktop/updates/macos/aarch64/0.0.0", { method: "GET" }, false);
        return extractDesktopDownloadLinks(manifest);
    }

    async listConversations(params?: { agentProfile?: string }) {
        const query = params?.agentProfile ? `?agent_profile=${encodeURIComponent(params.agentProfile)}` : "";
        return this.request<AssistantConversation[]>(`/api/v1/chat/conversations${query}`, { method: "GET" });
    }

    async createConversation(input: { sourceApp: string; title?: string; sessionContext?: Record<string, unknown> }) {
        return this.request<AssistantConversation>("/api/v1/chat/conversations", {
            method: "POST",
            body: JSON.stringify({
                source_app: input.sourceApp,
                title: input.title || "",
                session_context: input.sessionContext || {}
            })
        });
    }

    async getMessages(conversationId: string) {
        const payload = await this.request<AssistantMessage[] | PaginatedResponse<AssistantMessage>>(`/api/v1/chat/conversations/${conversationId}/messages`, { method: "GET" });
        return Array.isArray(payload) ? payload : payload.results;
    }

    /**
     * Vote sur une réponse assistant. Renvoyer le même `rating` que le vote actuel le retire (toggle).
     * `rating` : 1 pouce haut, -1 pouce bas.
     */
    async submitAssistantMessageFeedback(conversationId: string, messageId: number, rating: 1 | -1) {
        return this.request<{ feedback_rating: number | null }>(`/api/v1/chat/conversations/${conversationId}/messages/${messageId}/feedback`, { method: "POST", body: JSON.stringify({ rating }) });
    }

    async patchConversationSessionContext(conversationId: string, sessionContext: Record<string, unknown>) {
        return this.request<AssistantConversation>(`/api/v1/chat/conversations/${conversationId}`, {
            method: "PATCH",
            body: JSON.stringify({ session_context: sessionContext })
        });
    }

    async sendMessage(conversationId: string, content: string, sessionContext?: Record<string, unknown>) {
        return this.request<{ status: string; message_id: number }>(`/api/v1/chat/conversations/${conversationId}/send`, {
            method: "POST",
            body: JSON.stringify({
                content,
                ...(sessionContext ? { session_context: sessionContext } : {})
            })
        });
    }

    async streamConversation(conversationId: string, messageId: number, onEvent: (event: AssistantSseEvent) => void): Promise<void> {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            const token = await this.tokenManager.getValidToken(attempt > 0);
            const response = await fetch(joinUrl(this.baseUrl, `/api/v1/chat/conversations/${conversationId}/stream`), {
                method: "POST",
                credentials: "include",
                headers: {
                    Accept: "text/event-stream, application/json;q=0.9, */*;q=0.8",
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ message_id: messageId })
            });

            if (!response.ok) {
                const errorPayload = parseJsonOrRaw(await response.text());
                const shouldRetry = attempt === 0 && (response.status === 401 || response.status === 403);
                if (shouldRetry) {
                    this.tokenManager.clear();
                    continue;
                }
                throw new AssistantApiError(`Assistant stream error (${response.status})`, response.status, errorPayload);
            }

            if (!response.body) {
                throw new AssistantApiError("Assistant stream is empty", response.status, null);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parsed = parseSseChunk(buffer);
                buffer = parsed.rest;
                for (const event of parsed.events) {
                    onEvent({
                        eventType: event.eventType,
                        data: event.data,
                        raw: event.raw,
                        receivedAt: new Date().toISOString()
                    });
                }
            }

            if (buffer.trim()) {
                const parsed = parseSseChunk(`${buffer}\n\n`);
                for (const event of parsed.events) {
                    onEvent({
                        eventType: event.eventType,
                        data: event.data,
                        raw: event.raw,
                        receivedAt: new Date().toISOString()
                    });
                }
            }
            return;
        }

        throw new AssistantApiError("Assistant stream auth error", 401, null);
    }
}

export const walterAssistantClient = new WalterAssistantClient();
